import {
  OrderItemSearch,
  OrderItemSearchResult,
  OrderItemSummaryResult,
  SkuReports,
  SkuReportsResult,
  SkuReportsSearch,
  MissingStockSearch,
  MissingStockResult,
} from "@/types/OrderItem";
import {
  UnAssignSkuSearch,
  UnAssignSkuSearchResult,
} from "@/types/UnAssignSku";
import { marketPlaceBaseQueryWrapper } from "@/utils/rtkHelper";
import { createApi } from "@reduxjs/toolkit/query/react";

export const OrderItemApiAmazon = createApi({
  reducerPath: "OrderItemApiAmazon",
  baseQuery: marketPlaceBaseQueryWrapper,
  tagTypes: ["orderItemAmazon"],
  keepUnusedDataFor: 0,
  endpoints: (builder) => ({
    getOrderItem: builder.query({
      query: () => `/amazon-order`,
      providesTags: ["orderItemAmazon"],
    }),
    getDashboardCounts: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/amazon-order/dashboard-count/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItemAmazon"],
    }),
    getTotalOrdersChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/amazon-order/total-order-chart/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItemAmazon"],
    }),
    getChargeableAmtChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/amazon-order/chargeable-amounts/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItemAmazon"],
    }),
    getReturnStatusChartChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/amazon-order/return-status-chart/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItemAmazon"],
    }),
    getTop10SkuProfitChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/amazon-order/top-10-sku-profit-chart/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItemAmazon"],
    }),
    getTop10SkuLossChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/amazon-order/top-10-sku-loss-chart/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItemAmazon"],
    }),
    getTop10SalesSkuChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/amazon-order/top-10-sales-sku/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItemAmazon"],
    }),
    getTop10StateWiseCustomerReturnChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/amazon-order/top-10-customer-returns-state/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItemAmazon"],
    }),
    getTop10SkuOfCustomerReturnChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/amazon-order/top-10-sku-of-customer-return/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItemAmazon"],
    }),
    getTop10CityWiseSalesOrderChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/amazon-order/top-10-sales-order-city-wise/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItemAmazon"],
    }),
    getTop10StateWiseSalesOrderChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/amazon-order/top-10-sales-order-state-wise/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItemAmazon"],
    }),
    getTop10CustomerReturnChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/amazon-order/top-10-customer-return/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItemAmazon"],
    }),
    getRealTimeProfitChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/amazon-order/real-time-profit/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItemAmazon"],
    }),
    getUpcomingProfitChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/amazon-order/upcoming-profit/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItemAmazon"],
    }),
    getNetProfitChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/amazon-order/net-profit/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItemAmazon"],
    }),
    getDropdownOptions: builder.query({
      query: () => `/amazon-order/sku/sku-dropdown`,
      providesTags: ["orderItemAmazon"],
    }),
    searchAmazonOrders: builder.query<OrderItemSearchResult, OrderItemSearch>({
      query: (input) => ({
        url: `/amazon-order/ordersearch`,
        method: "POST",
        body: { ...input, export: false },
      }),
      providesTags: ["orderItemAmazon"],
    }),
    exportAmazonOrders: builder.mutation<Blob, OrderItemSearch>({
      query: (input) => ({
        url: `/amazon-order/ordersearch`,
        method: "POST",
        body: { ...input, export: true },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    summaryOrderItem: builder.query<OrderItemSummaryResult, OrderItemSearch>({
      query: (input) => ({
        url: `/amazon-order/summary`,
        method: "POST",
        body: input,
      }),
      providesTags: ["orderItemAmazon"],
    }),
    skuReportsAmazonOrders: builder.query<SkuReportsResult, SkuReportsSearch>({
      query: (input) => ({
        url: `/amazon-order/sku-report`,
        method: "POST",
        body: { ...input, export: false },
      }),
      providesTags: ["orderItemAmazon"],
    }),
    exportSkuReports: builder.mutation<Blob, SkuReportsSearch>({
      query: (input) => ({
        url: `/amazon-order/sku-report`,
        method: "POST",
        body: { ...input, export: true },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    summarySkuReport: builder.query<SkuReports, SkuReportsSearch>({
      query: (input) => ({
        url: `/amazon-order/sku-report/summary`,
        method: "POST",
        body: input,
      }),
      providesTags: ["orderItemAmazon"],
    }),
    deleteTable: builder.mutation({
      query: () => ({
        url: `/amazon-order/truncate-tables`,
        method: "POST",
      }),
      invalidatesTags: ["orderItemAmazon"],
    }),
    getUnAssignSku: builder.query<UnAssignSkuSearchResult, UnAssignSkuSearch>({
      query: (input) => ({
        url: `/amazon-order/unassigned-skus`,
        method: "POST",
        body: { ...input, export: false },
      }),
      providesTags: ["orderItemAmazon"],
    }),
    exportUnAssignSku: builder.mutation<Blob, UnAssignSkuSearch>({
      query: (input) => ({
        url: `/amazon-order/unassigned-skus`,
        method: "POST",
        body: { ...input, export: true },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    getMissingStock: builder.query<MissingStockResult, MissingStockSearch>({
      query: (input) => ({
        url: `/amazon-order/missing-stock`,
        method: "POST",
        body: { ...input, export: false },
      }),
      providesTags: ["orderItemAmazon"],
    }),
    exportMissingStock: builder.mutation<Blob, MissingStockSearch>({
      query: (input) => ({
        url: `/amazon-order/missing-stock`,
        method: "POST",
        body: { ...input, export: true },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    getMissingStockCount: builder.query<number, void>({
      query: () => `/amazon-order/missing_stock_count`,
      providesTags: ["orderItemAmazon"],
    }),
    getUniqueUnAssignSkus: builder.query<string[], void>({
      query: () => `/amazon-order/skus/unique-unassign-skus`,
      providesTags: ["orderItemAmazon"],
    }),
  }),
});

export const {
  useGetOrderItemQuery,
  useGetDashboardCountsQuery,
  useGetChargeableAmtChartQuery,
  useGetTop10SkuProfitChartQuery,
  useGetTop10SkuLossChartQuery,
  useGetReturnStatusChartChartQuery,
  useSearchAmazonOrdersQuery,
  useLazySearchAmazonOrdersQuery,
  useExportAmazonOrdersMutation,
  useSummaryOrderItemQuery,
  useSkuReportsAmazonOrdersQuery,
  useLazySkuReportsAmazonOrdersQuery,
  useExportSkuReportsMutation,
  useSummarySkuReportQuery,
  useDeleteTableMutation,
  useGetUnAssignSkuQuery,
  useLazyGetUnAssignSkuQuery,
  useExportUnAssignSkuMutation,
  useGetTotalOrdersChartQuery,
  useGetTop10StateWiseCustomerReturnChartQuery,
  useGetTop10SalesSkuChartQuery,
  useGetTop10SkuOfCustomerReturnChartQuery,
  useGetTop10CityWiseSalesOrderChartQuery,
  useGetTop10StateWiseSalesOrderChartQuery,
  useGetTop10CustomerReturnChartQuery,
  useGetDropdownOptionsQuery,
  useGetRealTimeProfitChartQuery,
  useGetNetProfitChartQuery,
  useGetUpcomingProfitChartQuery,
  useGetMissingStockQuery,
  useExportMissingStockMutation,
  useGetMissingStockCountQuery,
  useGetUniqueUnAssignSkusQuery,
  useLazyGetUniqueUnAssignSkusQuery,
} = OrderItemApiAmazon;
